import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Sparkles, FileText, Loader2, User, RotateCcw } from "lucide-react";
import type { LinkedInJob, TechManager } from "../api/LinkedInJobsApi";
import type { CoverLetterDocumentSlim } from "../api/LinkedInJobsApi";
import {
  fetchEmployeeCoverLettersForAdmin,
  enhanceCoverLetterForJob,
} from "../api/LinkedInJobsApi";
import { listCoverLetterDocuments, CoverLetterEnhanceResult } from "features/cover-letter/api/CoverLetterApi";
import EnhancedCoverLetterModal from "features/cover-letter/ui/EnhancedCoverLetterModal";
import CoverLetterEnhancementLogsModal from "./CoverLetterEnhancementLogsModal";

type Phase = "idle" | "enhancing" | "done" | "error";

export interface CoverLetterEnhancementSectionHandle {
  runEnhancement: () => Promise<void>;
  canEnhance: boolean;
}

interface Props {
  job: LinkedInJob;
  isAdmin: boolean;
  techManager?: TechManager | null;
  initialEnhancedData?: CoverLetterEnhanceResult | null;
  /** category_id of the CV currently selected in the sibling CvEnhancementSection
   * — drives the auto-match default (most-recently-updated cover letter sharing
   * that category). Pass null/undefined when no CV is selected or it has no category. */
  autoMatchCategoryId?: number | null;
  /** The CV currently selected in the sibling CvEnhancementSection — passed through
   * to the enhance-cover-letter call so grounding doesn't depend on the (much slower)
   * CV pipeline having already written its result back when both run concurrently via
   * a combined "Enhance All" trigger. */
  groundingCvId?: number | null;
  /** When false, hides this section's own idle-state "Enhance" button — same
   * convention as CvEnhancementSection's showOwnTrigger. */
  showOwnTrigger?: boolean;
}

const CoverLetterEnhancementSection = forwardRef<CoverLetterEnhancementSectionHandle, Props>(
  function CoverLetterEnhancementSection(
    { job, isAdmin, techManager, initialEnhancedData, autoMatchCategoryId, groundingCvId, showOwnTrigger = true },
    ref,
  ) {
  const [phase, setPhase] = useState<Phase>(initialEnhancedData ? "done" : "idle");
  const [coverLetterList, setCoverLetterList] = useState<CoverLetterDocumentSlim[] | null>(null);
  const [clLoading, setClLoading] = useState(false);
  const [selectedEmployeeCode, setSelectedEmployeeCode] = useState<string>("");
  const [selectedCoverLetterId, setSelectedCoverLetterId] = useState<number | "">("");
  const [manuallySet, setManuallySet] = useState(false);
  const [enhancedData, setEnhancedData] = useState<CoverLetterEnhanceResult | null>(initialEnhancedData ?? null);
  const [showModal, setShowModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [hasLogs, setHasLogs] = useState(job.has_cover_letter_enhancement_logs ?? false);
  const [alertId, setAlertId] = useState<number | undefined>(job.alert_id);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const phaseRef = useRef<Phase>(phase);

  useEffect(() => { return () => { mountedRef.current = false; }; }, []);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  useEffect(() => {
    if (job.has_cover_letter_enhancement_logs) setHasLogs(true);
    if (job.alert_id) setAlertId(job.alert_id);
  }, [job.has_cover_letter_enhancement_logs, job.alert_id]);

  useEffect(() => {
    if (initialEnhancedData && (phaseRef.current === "idle" || phaseRef.current === "error")) {
      setEnhancedData(initialEnhancedData);
      setPhase("done");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEnhancedData]);

  useEffect(() => {
    if (isAdmin && techManager) setSelectedEmployeeCode(techManager.employee_code);
  }, [isAdmin, techManager]);

  // Load cover letter list for non-admin on mount
  useEffect(() => {
    if (isAdmin) return;
    setClLoading(true);
    listCoverLetterDocuments()
      .then((docs) => {
        if (!mountedRef.current) return;
        setCoverLetterList(docs.map((d) => ({ id: d.id, title: d.title, category_id: d.category_id })));
        setClLoading(false);
      })
      .catch(() => { if (mountedRef.current) { setCoverLetterList([]); setClLoading(false); } });
  }, [isAdmin]);

  // Load cover letter list for admin when employee is selected
  useEffect(() => {
    if (!isAdmin || !selectedEmployeeCode) {
      if (isAdmin) { setCoverLetterList(null); setClLoading(false); setSelectedCoverLetterId(""); setManuallySet(false); }
      return;
    }
    setCoverLetterList(null);
    setClLoading(true);
    fetchEmployeeCoverLettersForAdmin(selectedEmployeeCode)
      .then((list) => { if (mountedRef.current) { setCoverLetterList(list); setClLoading(false); } })
      .catch(() => { if (mountedRef.current) { setCoverLetterList([]); setClLoading(false); } });
  }, [isAdmin, selectedEmployeeCode]);

  // Auto-match: pick the most-recently-updated cover letter sharing the selected
  // CV's category_id, unless the user has manually chosen one themselves. Cover
  // letters are already ordered most-recently-updated-first by the backend.
  useEffect(() => {
    if (manuallySet || phase !== "idle") return;
    if (!coverLetterList || coverLetterList.length === 0) return;
    if (autoMatchCategoryId == null) return;
    const match = coverLetterList.find((c) => c.category_id === autoMatchCategoryId);
    if (match) setSelectedCoverLetterId(match.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMatchCategoryId, coverLetterList, manuallySet, phase]);

  async function runEnhancement() {
    const coverLetterId = selectedCoverLetterId as number;
    if (!coverLetterId) return;
    if (isAdmin && !selectedEmployeeCode) return;
    setPhase("enhancing");
    setError(null);
    try {
      const result = await enhanceCoverLetterForJob(job.id, coverLetterId, isAdmin, isAdmin ? selectedEmployeeCode : undefined, groundingCvId);
      if (mountedRef.current) {
        setEnhancedData(result.enhanced_cover_letter_data as CoverLetterEnhanceResult);
        setPhase("done");
        setAlertId(result.alert_id);
        if (result.has_cover_letter_enhancement_logs) setHasLogs(true);
      }
    } catch (err: any) {
      if (mountedRef.current) { setError(err?.response?.data?.detail ?? "Enhancement failed. Please try again."); setPhase("error"); }
    }
  }

  async function reEnhance() {
    const coverLetterId = (selectedCoverLetterId as number) || job.cover_letter_id_enhanced;
    if (!coverLetterId) return;
    if (isAdmin && !selectedEmployeeCode) {
      setError("Cannot re-enhance: no employee is linked to this job.");
      return;
    }
    setPhase("enhancing");
    setError(null);
    try {
      const result = await enhanceCoverLetterForJob(job.id, coverLetterId, isAdmin, isAdmin ? selectedEmployeeCode : undefined, groundingCvId);
      if (mountedRef.current) {
        setEnhancedData(result.enhanced_cover_letter_data as CoverLetterEnhanceResult);
        setPhase("done");
        setAlertId(result.alert_id);
        if (result.has_cover_letter_enhancement_logs) setHasLogs(true);
      }
    } catch (err: any) {
      if (mountedRef.current) { setError(err?.response?.data?.detail ?? "Enhancement failed. Please try again."); setPhase("error"); }
    }
  }

  const canEnhance = selectedCoverLetterId !== "" && (!isAdmin || selectedEmployeeCode !== "");

  useImperativeHandle(ref, () => ({ runEnhancement, canEnhance }));

  // ── Done ────────────────────────────────────────────────────────────────────
  if (phase === "done" && enhancedData) {
    return (
      <>
        <div className="flex items-center gap-2 flex-wrap">
          <FileText className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
          <button
            onClick={() => setShowModal(true)}
            className="text-xs text-emerald-400 hover:text-emerald-300 font-inter font-medium underline underline-offset-2 transition-colors"
          >
            View Enhanced Cover Letter
          </button>
          {hasLogs && alertId && (
            <button
              onClick={() => setShowLogsModal(true)}
              className="text-xs text-muted-foreground hover:text-primary font-inter underline underline-offset-2 transition-colors"
            >
              Enhancement logs
            </button>
          )}
          <button
            onClick={reEnhance}
            title="Re-enhance with the same cover letter"
            className="flex items-center gap-1 px-1.5 py-0.5 text-muted-foreground hover:text-primary rounded transition-colors"
          >
            <RotateCcw className="w-3 h-3 shrink-0" />
            <span className="text-[10px] font-inter underline underline-offset-2">Re-Enhance</span>
          </button>
        </div>
        {showModal && (
          <EnhancedCoverLetterModal
            enhancedContent={enhancedData.content}
            overallScore={enhancedData.overall_score}
            critique={enhancedData.critique}
            topPriorityFix={enhancedData.top_priority_fix}
            jobTitle={job.job_title}
            companyName={job.company_name}
            isAdmin={isAdmin}
            employeeCode={isAdmin ? selectedEmployeeCode : undefined}
            onClose={() => setShowModal(false)}
          />
        )}
        {showLogsModal && alertId && (
          <CoverLetterEnhancementLogsModal alertId={alertId} isAdmin={isAdmin} onClose={() => setShowLogsModal(false)} />
        )}
      </>
    );
  }

  // ── Enhancing ───────────────────────────────────────────────────────────────
  if (phase === "enhancing") {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground font-inter">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-primary shrink-0" />
        Enhancing cover letter…
      </div>
    );
  }

  // ── Idle / error — inline dropdown ──────────────────────────────────────────
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="flex items-center gap-1.5 text-xs text-primary font-inter font-medium shrink-0">
          <Sparkles className="w-3.5 h-3.5 shrink-0" />
          {showOwnTrigger ? "Enhance cover letter for this job" : "Cover Letter"}
        </span>

        <select
          value={String(selectedCoverLetterId)}
          onChange={(e) => { setManuallySet(true); setSelectedCoverLetterId(e.target.value ? Number(e.target.value) : ""); }}
          disabled={clLoading || (isAdmin && !selectedEmployeeCode)}
          className="bg-surface-muted border border-border rounded-lg px-2 py-1 text-xs text-foreground font-inter cursor-pointer hover:border-border focus:outline-none focus:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">
            {clLoading
              ? "Loading…"
              : isAdmin && !selectedEmployeeCode
              ? "— select TM first —"
              : coverLetterList?.length === 0
              ? "No cover letters found"
              : "Select Cover Letter (Optional)"}
          </option>
          {(coverLetterList ?? []).map((cl) => (
            <option key={cl.id} value={String(cl.id)}>
              {cl.title}
            </option>
          ))}
        </select>

        {showOwnTrigger && (
          <button
            onClick={runEnhancement}
            disabled={!canEnhance}
            className="px-2.5 py-1 bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground text-xs font-inter font-medium rounded-lg transition-colors shrink-0"
          >
            Enhance
          </button>
        )}

        {techManager && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground font-inter shrink-0 ml-auto">
            <User className="w-3 h-3 shrink-0" />
            {techManager.name}
          </span>
        )}
      </div>
      {error && <p className="text-[11px] text-destructive font-inter mt-1">{error}</p>}
    </>
  );
});

export default CoverLetterEnhancementSection;
